import React, { useState } from 'react';
import {
  MdNotificationsNone, MdLogout, MdDoneAll, MdAutoAwesome, MdTour,
} from 'react-icons/md';
import { useAuth } from './auth';
import { useStudentData } from './StudentDataContext';
import { EmptyState } from '../ui/Card';
import { shortDate } from '../ui/format';
import { firstName } from './childStats';
import './shell.css';

/**
 * App frame: header + content + bottom tabs (phone) / side rail
 * (desktop). Navigation is a flat list of five tabs — shallow on
 * purpose, so nothing is ever more than one tap away.
 */
export default function Shell({
  nav, active, onNavigate, onReplayIntro, onOpenTour, children,
}) {
  const { user, parentType, logout } = useAuth();
  const {
    profile, notifications, unreadCount, markRead, markAllRead,
    family, currentId, hasSiblings, selectChild,
  } = useStudentData();
  const [inboxOpen, setInboxOpen] = useState(false);

  // A parent sees their child's name, not their own — the portal is
  // about the pupil, and the header is the only place that says which.
  const pupilName = profile?.name || user?.name || '';
  const viewerNote = parentType === 'father' ? 'ولي الأمر — الأب'
    : parentType === 'mother' ? 'ولي الأمر — الأم'
    : null;
  const section = [profile?.grade_title, profile?.section_label].filter(Boolean).join(' — ');
  const sub = hasSiblings ? [viewerNote, profile?.section_label].filter(Boolean).join(' · ') : (viewerNote || section);

  const openNotification = (n) => {
    if (!n.is_read) markRead(n.id);
  };

  return (
    <div className="shell">
      <header className="shell-top">
        <div className="shell-who">
          <strong className="shell-name">{pupilName}</strong>
          <span className="shell-sub">{sub || ' '}</span>
        </div>

        <button
          type="button"
          className="shell-bell"
          onClick={() => setInboxOpen((v) => !v)}
          aria-label={`الإشعارات${unreadCount ? ` (${unreadCount} غير مقروء)` : ''}`}
        >
          <MdNotificationsNone />
          {unreadCount > 0 && <span className="shell-bell-dot">{unreadCount > 9 ? '9+' : unreadCount}</span>}
        </button>

        {onOpenTour && (
          <button type="button" className="shell-bell" onClick={onOpenTour} aria-label="محطات بوابة مشكاة" title="محطات بوابة مشكاة">
            <MdTour />
          </button>
        )}

        {onReplayIntro && (
          <button type="button" className="shell-bell" onClick={onReplayIntro} aria-label="جولة الترحيب" title="جولة الترحيب">
            <MdAutoAwesome />
          </button>
        )}

        <button type="button" className="shell-bell" onClick={logout} aria-label="تسجيل الخروج">
          <MdLogout />
        </button>

        {hasSiblings && (
          <div className="shell-kids" role="tablist" aria-label="أبنائي">
            {family.map((c) => (
              <button
                key={c.id}
                type="button"
                role="tab"
                aria-selected={c.id === currentId}
                className={`shell-kid${c.id === currentId ? ' is-active' : ''}`}
                onClick={() => selectChild(c.id)}
              >
                <span className={`shell-kid-avatar is-${c.gender || 'none'}`}>{firstName(c).charAt(0)}</span>
                <span className="shell-kid-name">{firstName(c)}</span>
                {c.unread > 0 && <span className="shell-kid-dot" aria-label={`${c.unread} غير مقروء`} />}
              </button>
            ))}
          </div>
        )}
      </header>

      {inboxOpen && (
        <>
          <div className="shell-scrim" onClick={() => setInboxOpen(false)} />
          <div className="shell-inbox" role="dialog" aria-label="الإشعارات">
            <div className="shell-inbox-head">
              <strong>الإشعارات</strong>
              {unreadCount > 0 && (
                <button type="button" className="shell-inbox-all" onClick={markAllRead}>
                  <MdDoneAll /> تعليم الكل كمقروء
                </button>
              )}
            </div>
            <div className="shell-inbox-list">
              {notifications.length === 0 ? (
                <EmptyState icon={MdNotificationsNone} title="لا توجد إشعارات" />
              ) : notifications.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  className={`shell-note${n.is_read ? '' : ' is-unread'}`}
                  onClick={() => openNotification(n)}
                >
                  <div className="shell-note-top">
                    <strong>{n.title}</strong>
                    <span>{shortDate(n.created_at)}</span>
                  </div>
                  <p>{n.message}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Keyed by child: switching child replays the page's entrance, so
          the change of pupil is seen, not just the numbers swapping. */}
      <main className="shell-content" key={currentId}>{children}</main>

      <nav className="shell-tabs" aria-label="التنقل">
        {nav.map((item) => (
          <button
            key={item.id}
            type="button"
            className={`shell-tab${active === item.id ? ' is-active' : ''}`}
            onClick={() => onNavigate(item.id)}
            aria-current={active === item.id ? 'page' : undefined}
          >
            <span className="shell-tab-icon"><item.icon /></span>
            <span className="shell-tab-label">{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
